import React, { useState, useEffect } from 'react';
import {
  getWarehouseItems,
  createWarehouseItem,
  updateWarehouseItem,
  deleteWarehouseItem,
  addWarehouseItemVariant,
  updateWarehouseItemVariant,
  deleteWarehouseItemVariant
} from '../api';

function WarehouseManagement({ user }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [newItemName, setNewItemName] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [editingVariant, setEditingVariant] = useState(null);
  const [newVariantName, setNewVariantName] = useState('');
  const [newVariantType, setNewVariantType] = useState('size');
  const [newVariantQuantity, setNewVariantQuantity] = useState(0);
  const [newVariantAvailableStock, setNewVariantAvailableStock] = useState(0);
  const [newItemVariants, setNewItemVariants] = useState([]); // Varianten beim Erstellen
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024 && window.innerWidth > 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsTablet(window.innerWidth <= 1024 && window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (user && user.organization_id) {
      loadItems();
    }
  }, [user]);

  const loadItems = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await getWarehouseItems(user.id, user.organization_id);
      if (result.success) {
        setItems(result.items || []);
      } else {
        setError(result.error || 'Fehler beim Laden der Lagerpositionen');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateItem = async (e) => {
    e.preventDefault();
    if (!newItemName.trim()) {
      setError('Bitte geben Sie einen Namen ein');
      return;
    }

    try {
      // Erst die Position erstellen
      const result = await createWarehouseItem(
        user.id,
        user.organization_id,
        newItemName,
        newItemDescription
      );
      if (result.success) {
        const itemId = result.itemId;
        
        // Dann alle Varianten hinzufügen
        if (newItemVariants.length > 0) {
          for (const variant of newItemVariants) {
            if (variant.name && variant.name.trim()) {
              await addWarehouseItemVariant(
                user.id,
                itemId,
                variant.name,
                variant.type || 'size',
                variant.quantity || 0,
                variant.availableStock !== undefined ? variant.availableStock : (variant.quantity || 0)
              );
            }
          }
        }
        
        // Formular zurücksetzen
        setNewItemName('');
        setNewItemDescription('');
        setNewItemVariants([]);
        setShowCreateForm(false);
        
        // Items neu laden und dann das neue Item automatisch expandieren
        await loadItems();
        
        // Item automatisch expandieren, damit Varianten sichtbar sind
        if (itemId) {
          const newExpanded = new Set(expandedItems);
          newExpanded.add(itemId);
          setExpandedItems(newExpanded);
        }
      } else {
        setError(result.error || 'Fehler beim Erstellen der Position');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  const handleAddNewVariant = () => {
    setNewItemVariants([...newItemVariants, { name: '', type: 'size', quantity: 0, availableStock: 0 }]);
  };

  const handleRemoveNewVariant = (index) => {
    setNewItemVariants(newItemVariants.filter((_, i) => i !== index));
  };

  const handleUpdateNewVariant = (index, field, value) => {
    const updated = [...newItemVariants];
    updated[index][field] = value;
    setNewItemVariants(updated);
  };

  const handleUpdateItem = async (itemId, name, description) => {
    try {
      const result = await updateWarehouseItem(user.id, itemId, name, description);
      if (result.success) {
        loadItems();
        setEditingItem(null);
      } else {
        setError(result.error || 'Fehler beim Aktualisieren der Position');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Möchten Sie diese Position wirklich löschen? Alle Varianten werden ebenfalls gelöscht.')) {
      return;
    }

    try {
      const result = await deleteWarehouseItem(user.id, itemId);
      if (result.success) {
        loadItems();
      } else {
        setError(result.error || 'Fehler beim Löschen der Position');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  const handleAddVariant = async (itemId, e) => {
    e.preventDefault();
    if (!newVariantName.trim()) {
      setError('Bitte geben Sie einen Variantennamen ein');
      return;
    }

    if (newVariantAvailableStock > newVariantQuantity) {
      setError('Auf Lager darf nicht größer als Stückzahl sein');
      return;
    }

    try {
      const result = await addWarehouseItemVariant(
        user.id,
        itemId,
        newVariantName,
        newVariantType,
        newVariantQuantity,
        newVariantAvailableStock
      );
      if (result.success) {
        setNewVariantName('');
        setNewVariantType('size');
        setNewVariantQuantity(0);
        setNewVariantAvailableStock(0);
        setEditingVariant(null);
        loadItems();
      } else {
        setError(result.error || 'Fehler beim Hinzufügen der Variante');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  const handleUpdateVariant = async (variantId, variantName, variantType, stockQuantity, availableStock) => {
    try {
      const result = await updateWarehouseItemVariant(
        user.id,
        variantId,
        variantName,
        variantType,
        stockQuantity,
        availableStock
      );
      if (result.success) {
        loadItems();
        setEditingVariant(null);
      } else {
        setError(result.error || 'Fehler beim Aktualisieren der Variante');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  const handleDeleteVariant = async (variantId) => {
    if (!window.confirm('Möchten Sie diese Variante wirklich löschen?')) {
      return;
    }

    try {
      const result = await deleteWarehouseItemVariant(user.id, variantId);
      if (result.success) {
        loadItems();
      } else {
        setError(result.error || 'Fehler beim Löschen der Variante');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    }
  };

  const toggleItemExpansion = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const containerStyles = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
  };

  const headerStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  };

  const buttonStyles = {
    padding: '6px 12px', // Reduced padding
    background: '#0a9a22',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: '500',
    width: isMobile ? '100%' : 'auto',
    fontSize: '13px', // Slightly smaller
  };

  const cardStyles = {
    backgroundColor: 'white',
    border: '1px solid #e2e8f0',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '16px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  };

  const itemHeaderStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    cursor: 'pointer',
  };

  const variantStyles = {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #e2e8f0',
  };

  const variantItemStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #f1f5f9',
  };

  if (loading) {
    return (
      <div style={containerStyles}>
        <div style={{ textAlign: 'center', padding: '40px' }}>Laden...</div>
      </div>
    );
  }

  return (
    <div style={containerStyles}>
      <div style={headerStyles}>
        <h1 style={{ margin: 0 }}>Lagerverwaltung</h1>
        <button
          style={buttonStyles}
          onClick={() => {
            setShowCreateForm(!showCreateForm);
            if (showCreateForm) {
              // Formular zurücksetzen beim Abbrechen
              setNewItemName('');
              setNewItemDescription('');
              setNewItemVariants([]);
            }
          }}
        >
          {showCreateForm ? 'Abbrechen' : '+ Neue Position'}
        </button>
      </div>

      {error && (
        <div style={{ padding: '12px', backgroundColor: '#fee', color: '#c33', borderRadius: '6px', marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {showCreateForm && (
        <div style={cardStyles}>
          <h3 style={{ marginTop: 0 }}>Neue Position erstellen</h3>
          <form onSubmit={handleCreateItem}>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Name *
              </label>
              <input
                type="text"
                value={newItemName}
                onChange={(e) => setNewItemName(e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                required
              />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                Beschreibung
              </label>
              <textarea
                value={newItemDescription}
                onChange={(e) => setNewItemDescription(e.target.value)}
                style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', minHeight: '80px' }}
              />
            </div>
            
            {/* Varianten beim Erstellen */}
            <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <label style={{ fontWeight: '500' }}>Varianten/Größen (optional)</label>
                <button
                  type="button"
                  onClick={handleAddNewVariant}
                  style={{ ...buttonStyles, backgroundColor: '#64748b', padding: '6px 12px', fontSize: '12px' }}
                >
                  + Variante hinzufügen
                </button>
              </div>
              
              {newItemVariants.length > 0 && (
                <div>
                  {newItemVariants.map((variant, index) => (
                    <div key={index} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '8px', alignItems: 'end', marginBottom: '8px' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Name (z.B. Rot, Größe M)</label>
                        <input
                          type="text"
                          value={variant.name}
                          onChange={(e) => handleUpdateNewVariant(index, 'name', e.target.value)}
                          style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                          placeholder="z.B. Rot"
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Typ</label>
                        <select
                          value={variant.type || 'size'}
                          onChange={(e) => handleUpdateNewVariant(index, 'type', e.target.value)}
                          style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                        >
                          <option value="size">Größe</option>
                          <option value="color">Farbe</option>
                          <option value="other">Sonstiges</option>
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Stückzahl</label>
                        <input
                          type="number"
                          value={variant.quantity || 0}
                          onChange={(e) => {
                            const val = parseInt(e.target.value) || 0;
                            handleUpdateNewVariant(index, 'quantity', val);
                            if ((variant.availableStock || 0) > val) {
                              handleUpdateNewVariant(index, 'availableStock', val);
                            }
                          }}
                          style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                          min="0"
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Auf Lager</label>
                        <input
                          type="number"
                          value={variant.availableStock !== undefined ? variant.availableStock : 0}
                          onChange={(e) => handleUpdateNewVariant(index, 'availableStock', parseInt(e.target.value) || 0)}
                          style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                          min="0"
                          max={variant.quantity || 0}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveNewVariant(index)}
                        style={{ padding: '6px 12px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                      >
                        Entfernen
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <button type="submit" style={buttonStyles}>
              Erstellen
            </button>
          </form>
        </div>
      )}

      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
          Noch keine Lagerpositionen vorhanden. Erstellen Sie Ihre erste Position.
        </div>
      ) : (
        items.map((item) => (
          <div key={item.id} style={cardStyles}>
            {editingItem === item.id ? (
              <EditItemForm
                item={item}
                user={user}
                onSave={(name, description) => handleUpdateItem(item.id, name, description)}
                onCancel={() => setEditingItem(null)}
                onVariantAdded={() => loadItems()}
                onVariantUpdated={() => loadItems()}
                onVariantDeleted={() => loadItems()}
                addWarehouseItemVariant={addWarehouseItemVariant}
                updateWarehouseItemVariant={updateWarehouseItemVariant}
                deleteWarehouseItemVariant={deleteWarehouseItemVariant}
              />
            ) : (
              <>
                <div style={itemHeaderStyles} onClick={() => toggleItemExpansion(item.id)}>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ margin: '0 0 4px 0' }}>{item.name}</h3>
                    {item.description && (
                      <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>
                        {item.description}
                      </p>
                    )}
                    {/* Varianten-Anzeige auch in der normalen Ansicht */}
                    {item.variants && item.variants.length > 0 && (
                      <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                        {item.variants.map((variant) => (
                          <span
                            key={variant.id}
                            style={{
                              display: 'inline-block',
                              padding: '4px 8px',
                              backgroundColor: '#f1f5f9',
                              color: '#475569',
                              borderRadius: '4px',
                              fontSize: '12px',
                              border: '1px solid #e2e8f0'
                            }}
                          >
                            {variant.variant_name} ({variant.variant_type}) - Stückzahl: {variant.stock_quantity}, Auf Lager: {variant.available_stock || 0}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingItem(item.id);
                      }}
                      style={{ ...buttonStyles, backgroundColor: '#64748b', padding: '6px 12px', fontSize: '12px' }}
                    >
                      Bearbeiten
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteItem(item.id);
                      }}
                      style={{ ...buttonStyles, backgroundColor: '#ef4444', padding: '6px 12px', fontSize: '12px' }}
                    >
                      Löschen
                    </button>
                  </div>
                </div>

                {expandedItems.has(item.id) && (
                  <div style={variantStyles}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <h4 style={{ margin: 0 }}>Varianten/Größen</h4>
                      <button
                        onClick={() => {
                          setEditingVariant({ itemId: item.id, mode: 'add' });
                          setNewVariantName('');
                          setNewVariantType('size');
                          setNewVariantQuantity(0);
                        }}
                        style={{ ...buttonStyles, padding: '6px 12px', fontSize: '12px' }}
                      >
                        + Variante hinzufügen
                      </button>
                    </div>

                    {editingVariant && editingVariant.itemId === item.id && editingVariant.mode === 'add' && (
                      <div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '6px', marginBottom: '12px' }}>
                        <form onSubmit={(e) => handleAddVariant(item.id, e)}>
                          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '8px', alignItems: 'end' }}>
                            <div>
                              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Name (z.B. Rot, Größe M)</label>
                              <input
                                type="text"
                                value={newVariantName}
                                onChange={(e) => setNewVariantName(e.target.value)}
                                style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                                placeholder="z.B. Rot"
                                required
                              />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Typ</label>
                              <select
                                value={newVariantType}
                                onChange={(e) => setNewVariantType(e.target.value)}
                                style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                              >
                                <option value="size">Größe</option>
                                <option value="color">Farbe</option>
                                <option value="other">Sonstiges</option>
                              </select>
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Stückzahl</label>
                              <input
                                type="number"
                                value={newVariantQuantity}
                                onChange={(e) => setNewVariantQuantity(parseInt(e.target.value) || 0)}
                                style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                                min="0"
                              />
                            </div>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button type="submit" style={{ ...buttonStyles, padding: '6px 12px', fontSize: '12px' }}>
                                Speichern
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingVariant(null)}
                                style={{ ...buttonStyles, backgroundColor: '#64748b', padding: '6px 12px', fontSize: '12px' }}
                              >
                                Abbrechen
                              </button>
                            </div>
                          </div>
                        </form>
                      </div>
                    )}

                    {item.variants && item.variants.length > 0 ? (
                      item.variants.map((variant) => (
                        <div key={variant.id} style={variantItemStyles}>
                          {editingVariant && editingVariant.variantId === variant.id ? (
                            <EditVariantForm
                              variant={variant}
                              onSave={(name, type, quantity, availableStock) => handleUpdateVariant(variant.id, name, type, quantity, availableStock)}
                              onCancel={() => setEditingVariant(null)}
                            />
                          ) : (
                            <>
                              <div>
                                <strong>{variant.variant_name}</strong>
                                <span style={{ marginLeft: '8px', color: '#64748b', fontSize: '12px' }}>
                                  ({variant.variant_type})
                                </span>
                                <span style={{ marginLeft: '12px', color: '#64748b' }}>
                                  Stückzahl: {variant.stock_quantity}
                                </span>
                              </div>
                              <div style={{ display: 'flex', gap: '4px' }}>
                                <button
                                  onClick={() => setEditingVariant({ variantId: variant.id, mode: 'edit', variant })}
                                  style={{ ...buttonStyles, backgroundColor: '#64748b', padding: '4px 8px', fontSize: '11px' }}
                                >
                                  Bearbeiten
                                </button>
                                <button
                                  onClick={() => handleDeleteVariant(variant.id)}
                                  style={{ ...buttonStyles, backgroundColor: '#ef4444', padding: '4px 8px', fontSize: '11px' }}
                                >
                                  Löschen
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      ))
                    ) : (
                      <div style={{ color: '#64748b', fontSize: '14px', padding: '8px 0' }}>
                        Noch keine Varianten vorhanden
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        ))
      )}
    </div>
  );
}

function EditItemForm({ item, user, onSave, onCancel, onVariantAdded, onVariantUpdated, onVariantDeleted, addWarehouseItemVariant, updateWarehouseItemVariant, deleteWarehouseItemVariant }) {
  const [name, setName] = useState(item.name);
  const [description, setDescription] = useState(item.description || '');
  const [editingVariant, setEditingVariant] = useState(null);
  const [newVariantName, setNewVariantName] = useState('');
  const [newVariantType, setNewVariantType] = useState('size');
  const [newVariantQuantity, setNewVariantQuantity] = useState(0);
  const [newVariantAvailableStock, setNewVariantAvailableStock] = useState(0);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(name, description);
  };

  const handleAddVariant = async (e) => {
    e.preventDefault();
    if (!newVariantName.trim()) {
      return;
    }

    if (newVariantAvailableStock > newVariantQuantity) {
      alert('Auf Lager darf nicht größer als Stückzahl sein');
      return;
    }

    try {
      const result = await addWarehouseItemVariant(
        user.id,
        item.id,
        newVariantName,
        newVariantType,
        newVariantQuantity,
        newVariantAvailableStock
      );
      if (result.success) {
        setNewVariantName('');
        setNewVariantType('size');
        setNewVariantQuantity(0);
        setNewVariantAvailableStock(0);
        setEditingVariant(null);
        onVariantAdded();
      }
    } catch (err) {
      console.error('Fehler beim Hinzufügen der Variante:', err);
    }
  };

  const handleUpdateVariant = async (variantId, variantName, variantType, stockQuantity, availableStock) => {
    try {
      const result = await updateWarehouseItemVariant(
        user.id,
        variantId,
        variantName,
        variantType,
        stockQuantity,
        availableStock
      );
      if (result.success) {
        setEditingVariant(null);
        onVariantUpdated();
      } else {
        alert(result.error || 'Fehler beim Aktualisieren der Variante');
      }
    } catch (err) {
      console.error('Fehler beim Aktualisieren der Variante:', err);
      alert('Fehler beim Aktualisieren der Variante: ' + err.message);
    }
  };

  const handleDeleteVariant = async (variantId) => {
    if (!window.confirm('Möchten Sie diese Variante wirklich löschen?')) {
      return;
    }

    try {
      const result = await deleteWarehouseItemVariant(user.id, variantId);
      if (result.success) {
        onVariantDeleted();
      }
    } catch (err) {
      console.error('Fehler beim Löschen der Variante:', err);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Name *</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
            required
          />
        </div>
        <div style={{ marginBottom: '12px' }}>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>Beschreibung</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '4px', minHeight: '80px' }}
          />
        </div>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
          <button type="submit" style={{ padding: '8px 16px', backgroundColor: '#4a6de5', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            Speichern
          </button>
          <button type="button" onClick={onCancel} style={{ padding: '8px 16px', backgroundColor: '#64748b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            Abbrechen
          </button>
        </div>
      </form>

      {/* Varianten-Verwaltung */}
      <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h4 style={{ margin: 0 }}>Varianten/Größen</h4>
          <button
            style={{ padding: '6px 12px', background: '#0a9a22', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: '500', fontSize: '13px', width: 'auto', flexShrink: 0, whiteSpace: 'nowrap' }}
            type="button"
            onClick={() => {
              setEditingVariant({ itemId: item.id, mode: 'add' });
              setNewVariantName('');
              setNewVariantType('size');
              setNewVariantQuantity(0);
              setNewVariantAvailableStock(0);
            }}
          >
            + Variante hinzufügen
          </button>
        </div>

        {editingVariant && editingVariant.itemId === item.id && editingVariant.mode === 'add' && (
          <div style={{ padding: '12px', backgroundColor: '#f8fafc', borderRadius: '6px', marginBottom: '12px' }}>
            <form onSubmit={handleAddVariant}>
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '8px', alignItems: 'end' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Name (z.B. Rot, Größe M)</label>
                  <input
                    type="text"
                    value={newVariantName}
                    onChange={(e) => setNewVariantName(e.target.value)}
                    style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                    placeholder="z.B. Rot"
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Typ</label>
                  <select
                    value={newVariantType}
                    onChange={(e) => setNewVariantType(e.target.value)}
                    style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                  >
                    <option value="size">Größe</option>
                    <option value="color">Farbe</option>
                    <option value="other">Sonstiges</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Stückzahl</label>
                  <input
                    type="number"
                    value={newVariantQuantity}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      setNewVariantQuantity(val);
                      if (newVariantAvailableStock > val) {
                        setNewVariantAvailableStock(val);
                      }
                    }}
                    style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                    min="0"
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px' }}>Auf Lager</label>
                  <input
                    type="number"
                    value={newVariantAvailableStock}
                    onChange={(e) => setNewVariantAvailableStock(parseInt(e.target.value) || 0)}
                    style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
                    min="0"
                    max={newVariantQuantity}
                  />
                </div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  <button type="submit" style={{ padding: '6px 12px', backgroundColor: '#4a6de5', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}>
                    Speichern
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingVariant(null);
                      setNewVariantAvailableStock(0);
                    }}
                    style={{ padding: '6px 12px', backgroundColor: '#64748b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            </form>
          </div>
        )}

        {item.variants && item.variants.length > 0 ? (
          item.variants.map((variant) => (
            <div key={variant.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
              {editingVariant && editingVariant.variantId === variant.id ? (
                <EditVariantForm
                  variant={variant}
                  onSave={(name, type, quantity, availableStock) => handleUpdateVariant(variant.id, name, type, quantity, availableStock)}
                  onCancel={() => setEditingVariant(null)}
                />
              ) : (
                <>
                  <div>
                    <strong>{variant.variant_name}</strong>
                    <span style={{ marginLeft: '8px', color: '#64748b', fontSize: '12px' }}>
                      ({variant.variant_type})
                    </span>
                    <span style={{ marginLeft: '12px', color: '#64748b' }}>
                      Stückzahl: {variant.stock_quantity}, Auf Lager: {variant.available_stock || 0}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      onClick={() => setEditingVariant({ variantId: variant.id, mode: 'edit', variant })}
                      style={{ padding: '4px 8px', backgroundColor: '#64748b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}
                    >
                      Bearbeiten
                    </button>
                    <button
                      onClick={() => handleDeleteVariant(variant.id)}
                      style={{ padding: '4px 8px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}
                    >
                      Löschen
                    </button>
                  </div>
                </>
              )}
            </div>
          ))
        ) : (
          <div style={{ color: '#64748b', fontSize: '14px', padding: '8px 0' }}>
            Noch keine Varianten vorhanden
          </div>
        )}
      </div>
    </div>
  );
}

function EditVariantForm({ variant, onSave, onCancel }) {
  const [name, setName] = useState(variant.variant_name);
  const [type, setType] = useState(variant.variant_type);
  const [quantity, setQuantity] = useState(variant.stock_quantity);
  const [availableStock, setAvailableStock] = useState(variant.available_stock || 0);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (availableStock > quantity) {
      alert('Auf Lager darf nicht größer als Stückzahl sein');
      return;
    }
    onSave(name, type, quantity, availableStock);
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '8px', alignItems: 'end', width: '100%' }}>
      <div>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
          required
        />
      </div>
      <div>
        <select
          value={type}
          onChange={(e) => setType(e.target.value)}
          style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
        >
          <option value="size">Größe</option>
          <option value="color">Farbe</option>
          <option value="other">Sonstiges</option>
        </select>
      </div>
      <div>
        <label style={{ display: 'block', marginBottom: '2px', fontSize: '10px', color: '#64748b' }}>Stückzahl</label>
        <input
          type="number"
          value={quantity}
          onChange={(e) => {
            const val = parseInt(e.target.value) || 0;
            setQuantity(val);
            if (availableStock > val) {
              setAvailableStock(val);
            }
          }}
          style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
          min="0"
        />
      </div>
      <div>
        <label style={{ display: 'block', marginBottom: '2px', fontSize: '10px', color: '#64748b' }}>Auf Lager</label>
        <input
          type="number"
          value={availableStock}
          onChange={(e) => setAvailableStock(parseInt(e.target.value) || 0)}
          style={{ width: '100%', padding: '6px', border: '1px solid #e2e8f0', borderRadius: '4px' }}
          min="0"
          max={quantity}
        />
      </div>
      <div style={{ display: 'flex', gap: '4px' }}>
        <button type="submit" style={{ padding: '4px 8px', backgroundColor: '#4a6de5', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
          Speichern
        </button>
        <button type="button" onClick={onCancel} style={{ padding: '4px 8px', backgroundColor: '#64748b', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
          Abbrechen
        </button>
      </div>
    </form>
  );
}

export default WarehouseManagement;
